import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Database, Users, Mail, Phone, MessageSquare, Plus, Trash2, ToggleLeft, ToggleRight, Briefcase, Building, UserCheck, Link2, ExternalLink, Send, Check, Loader2, ChevronDown, HelpCircle, Webhook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUILT_IN_FIELDS = [
  { id: 'name', label: 'Name', icon: UserCheck, type: 'text', placeholder: 'Your name' },
  { id: 'email', label: 'Email', icon: Mail, type: 'email', placeholder: 'Your email' },
  { id: 'contact', label: 'Contact', icon: Phone, type: 'tel', placeholder: 'Phone number' },
  { id: 'profession', label: 'Profession', icon: Briefcase, type: 'text', placeholder: 'Your profession' },
  { id: 'company', label: 'Company Name', icon: Building, type: 'text', placeholder: 'Company name' },
];

const CRM_INTEGRATIONS = [
  {
    id: 'hubspot', name: 'HubSpot', color: '#FF7A59',
    desc: 'Marketing, sales, and service CRM',
    helpUrl: 'https://knowledge.hubspot.com/integrations/how-do-i-get-my-hubspot-api-key',
    helpText: 'Settings → Integrations → Private Apps → Create token',
    apiFields: [
      { key: 'hubspot_access_token', label: 'Access Token', type: 'password', placeholder: 'pat-na1-xxxxxxxx' },
    ],
  },
  {
    id: 'salesforce', name: 'Salesforce', color: '#00A1E0',
    desc: 'Enterprise CRM platform',
    helpUrl: 'https://help.salesforce.com/s/articleView?id=sf.remoteaccess_oauth_tokens.htm',
    helpText: 'Setup → Apps → Connected Apps → Generate token',
    apiFields: [
      { key: 'salesforce_instance_url', label: 'Instance URL', type: 'text', placeholder: 'https://yourcompany.salesforce.com' },
      { key: 'salesforce_access_token', label: 'Access Token', type: 'password', placeholder: 'Your Salesforce token' },
    ],
  },
  {
    id: 'zoho', name: 'Zoho CRM', color: '#DC2626',
    desc: 'All-in-one business CRM',
    helpUrl: 'https://www.zoho.com/crm/developer/docs/api/v2/oauth-overview.html',
    helpText: 'Setup → Developer → APIs → Generate OAuth token',
    apiFields: [
      { key: 'zoho_access_token', label: 'Access Token', type: 'password', placeholder: '1000.xxxxxxxx' },
    ],
  },
  {
    id: 'pipedrive', name: 'Pipedrive', color: '#21A179',
    desc: 'Sales-focused CRM',
    helpUrl: 'https://pipedrive.readme.io/docs/how-to-find-the-api-token',
    helpText: 'Settings → Personal preferences → API → Your personal API token',
    apiFields: [
      { key: 'pipedrive_api_token', label: 'API Token', type: 'password', placeholder: 'Your Pipedrive API token' },
      { key: 'pipedrive_domain', label: 'Domain', type: 'text', placeholder: 'https://yourcompany.pipedrive.com' },
    ],
  },
  {
    id: 'pardot', name: 'Pardot', color: '#00A1E0',
    desc: 'Salesforce B2B marketing automation',
    helpUrl: 'https://help.salesforce.com/s/articleView?id=sf.pardot_sf_connector.htm',
    helpText: 'Setup → Pardot → Business Unit Setup → Copy Business Unit ID',
    apiFields: [
      { key: 'pardot_business_unit_id', label: 'Business Unit ID', type: 'text', placeholder: '0Uv...' },
      { key: 'pardot_access_token', label: 'Salesforce Access Token', type: 'password', placeholder: 'Your Salesforce token (Pardot uses SF auth)' },
    ],
  },
  {
    id: 'marketo', name: 'Marketo', color: '#5C4C9F',
    desc: 'Adobe marketing automation',
    helpUrl: 'https://developers.marketo.com/rest-api/authentication/',
    helpText: 'Admin → Integration → LaunchPoint → New Service → Get Client ID/Secret',
    apiFields: [
      { key: 'marketo_munchkin_id', label: 'Munchkin Account ID', type: 'text', placeholder: '123-ABC-456' },
      { key: 'marketo_client_id', label: 'Client ID', type: 'password', placeholder: 'Your Marketo Client ID' },
      { key: 'marketo_client_secret', label: 'Client Secret', type: 'password', placeholder: 'Your Marketo Client Secret' },
    ],
  },
  {
    id: 'zapier', name: 'Zapier', color: '#FF4A00',
    desc: 'Connect 5000+ apps via webhooks',
    helpUrl: 'https://zapier.com/apps/webhook/integrations',
    helpText: 'Create Zap → Trigger: Webhooks by Zapier → Catch Hook → Copy URL',
    apiFields: [
      { key: 'zapier_webhook_url', label: 'Zapier Webhook URL', type: 'text', placeholder: 'https://hooks.zapier.com/hooks/catch/...' },
    ],
    webhookOnly: true,
  },
  {
    id: 'slack', name: 'Slack', color: '#4A154B',
    desc: 'Get lead notifications in Slack',
    helpUrl: 'https://api.slack.com/messaging/webhooks',
    helpText: 'Slack App → Incoming Webhooks → Activate → Add to channel → Copy URL',
    apiFields: [
      { key: 'slack_webhook_url', label: 'Incoming Webhook URL', type: 'text', placeholder: 'https://hooks.slack.com/services/T.../B.../xxx' },
      { key: 'slack_channel_name', label: 'Channel Name (optional)', type: 'text', placeholder: '#leads' },
    ],
    webhookOnly: true,
  },
];

const CRMIntegration = ({ profile, setProfile }) => {
  const [tab, setTab] = useState('integrations');
  const [activeCRM, setActiveCRM] = useState(null);
  const [crmType, setCrmType] = useState('none');
  const [integrationMode, setIntegrationMode] = useState('api');
  const [crmSettings, setCrmSettings] = useState({});
  const [showContactForm, setShowContactForm] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [fieldToggles, setFieldToggles] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [newCustomField, setNewCustomField] = useState('');
  const [saving, setSaving] = useState(false);
  const [requestName, setRequestName] = useState('');
  const [requestDesc, setRequestDesc] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [requestSending, setRequestSending] = useState(false);

  useEffect(() => { fetchCRMSettings(); fetchRequests(); }, []);

  useEffect(() => {
    if (profile) {
      setShowContactForm(profile.show_contact_form || false);
      setWhatsappNumber(profile.whatsapp_number || '');
      setWhatsappMessage(profile.whatsapp_message || '');
      const fields = profile.contact_form_fields || [];
      const toggles = {};
      const custom = [];
      BUILT_IN_FIELDS.forEach(f => { toggles[f.id] = false; });
      fields.forEach(f => {
        if (BUILT_IN_FIELDS.find(b => b.id === f.id)) {
          toggles[f.id] = f.enabled !== false;
        } else {
          custom.push({ id: f.id, label: f.label, enabled: f.enabled !== false });
        }
      });
      setFieldToggles(toggles);
      setCustomFields(custom);
    }
  }, [profile]);

  const fetchCRMSettings = async () => {
    try {
      const res = await axios.get(`${API}/crm-settings`);
      setCrmType(res.data.crm_type || 'none');
      setIntegrationMode(res.data.integration_mode || 'api');
      setCrmSettings(res.data);
      if (res.data.crm_type && res.data.crm_type !== 'none') {
        setActiveCRM(res.data.crm_type);
      }
    } catch (e) {
      console.error('Failed to fetch CRM settings:', e);
    }
  };

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/crm-requests`);
      setMyRequests(res.data);
    } catch (e) { /* silent */ }
  };

  const buildFieldsArray = (toggles, custom) => {
    const fields = [];
    BUILT_IN_FIELDS.forEach(f => {
      fields.push({ id: f.id, label: f.label, type: f.type, placeholder: f.placeholder, builtin: true, enabled: !!toggles[f.id] });
    });
    custom.forEach(c => {
      fields.push({ id: c.id, label: c.label, type: 'text', placeholder: c.label, builtin: false, enabled: c.enabled !== false });
    });
    return fields;
  };

  const handleSaveContactSettings = async () => {
    setSaving(true);
    try {
      const fieldsArray = buildFieldsArray(fieldToggles, customFields);
      const response = await axios.put(`${API}/profile`, {
        show_contact_form: showContactForm,
        contact_form_fields: fieldsArray,
        whatsapp_number: whatsappNumber || null,
        whatsapp_message: whatsappMessage || null,
      });
      setProfile(response.data);
      toast.success('Exchange Contact settings saved');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectCRM = (crmId) => {
    if (activeCRM === crmId) {
      setActiveCRM(null);
      return;
    }
    setActiveCRM(crmId);
    const crm = CRM_INTEGRATIONS.find(c => c.id === crmId);
    if (crm?.webhookOnly) setIntegrationMode('api');
  };

  const handleSaveCRM = async () => {
    setSaving(true);
    const crm = CRM_INTEGRATIONS.find(c => c.id === activeCRM);
    if (!crm) { setSaving(false); return; }

    const payload = { crm_type: activeCRM, integration_mode: integrationMode };

    if (integrationMode === 'webhook' && !crm.webhookOnly) {
      payload.webhook_url = crmSettings.webhook_url || '';
    } else {
      crm.apiFields.forEach(f => {
        const val = crmSettings[f.key];
        if (val && !val.startsWith('****')) payload[f.key] = val;
      });
    }

    try {
      await axios.put(`${API}/crm-settings`, payload);
      toast.success(`${crm.name} integration saved!`);
      setCrmType(activeCRM);
    } catch (e) {
      toast.error('Failed to save CRM settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnectCRM = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/crm-settings`, { crm_type: 'none' });
      setCrmType('none');
      setCrmSettings({});
      setActiveCRM(null);
      toast.success('CRM disconnected');
    } catch (e) {
      toast.error('Failed to disconnect');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestName.trim()) { toast.error('Please enter a CRM name'); return; }
    setRequestSending(true);
    try {
      const res = await axios.post(`${API}/crm-requests`, { crm_name: requestName, description: requestDesc });
      setMyRequests([res.data, ...myRequests]);
      setRequestName('');
      setRequestDesc('');
      toast.success('Request submitted! We\'ll review it soon.');
    } catch (e) {
      toast.error('Failed to submit request');
    } finally {
      setRequestSending(false);
    }
  };

  const handleToggleField = (fieldId) => setFieldToggles(prev => ({ ...prev, [fieldId]: !prev[fieldId] }));
  const handleToggleCustomField = (fieldId) => setCustomFields(prev => prev.map(f => f.id === fieldId ? { ...f, enabled: !f.enabled } : f));
  const handleAddCustomField = () => {
    const label = newCustomField.trim();
    if (!label) return;
    setCustomFields(prev => [...prev, { id: 'custom_' + label.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now(), label, enabled: true }]);
    setNewCustomField('');
  };
  const handleRemoveCustomField = (fieldId) => setCustomFields(prev => prev.filter(f => f.id !== fieldId));

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="font-outfit font-bold text-2xl text-white">CRM & Contact Settings</h1>
        <p className="text-[#A1A1AA] mt-1 text-sm">Manage contact form, CRM integrations, and lead sync</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'integrations', label: 'Integrations' },
          { key: 'contact', label: 'Contact Form' },
          { key: 'request', label: 'Request CRM' },
        ].map(t => (
          <button key={t.key} data-testid={`crm-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              tab === t.key ? 'bg-violet-600 text-white' : 'bg-[#1E1E1E] text-[#A1A1AA] hover:text-white border border-[#27272A]'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══ INTEGRATIONS TAB ═══ */}
      {tab === 'integrations' && (
        <div className="space-y-4">
          {/* Active CRM indicator */}
          {crmType !== 'none' && (
            <div className="flex items-center justify-between p-4 rounded-xl bg-green-500/5 border border-green-500/20">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
                <span className="text-green-400 text-sm font-medium">
                  Connected: {CRM_INTEGRATIONS.find(c => c.id === crmType)?.name || crmType}
                </span>
              </div>
              <Button data-testid="disconnect-crm" variant="outline" size="sm" onClick={handleDisconnectCRM} disabled={saving}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs h-8">
                Disconnect
              </Button>
            </div>
          )}

          {/* CRM Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CRM_INTEGRATIONS.map(crm => {
              const isActive = activeCRM === crm.id;
              const isConnected = crmType === crm.id;
              return (
                <motion.div key={crm.id} layout>
                  <button data-testid={`crm-card-${crm.id}`}
                    onClick={() => handleSelectCRM(crm.id)}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isActive ? 'bg-[#1a1a2e] border-violet-500/50' :
                      isConnected ? 'bg-[#121212] border-green-500/30' :
                      'bg-[#121212] border-[#27272A] hover:border-[#3f3f46]'
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ background: `${crm.color}20` }}>
                        <Database className="w-5 h-5" style={{ color: crm.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium text-sm">{crm.name}</span>
                          {isConnected && <Check className="w-3.5 h-3.5 text-green-400" />}
                        </div>
                        <p className="text-[#71717A] text-xs truncate">{crm.desc}</p>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-[#525252] transition-transform ${isActive ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* Expanded Setup */}
                  {isActive && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                      className="mt-2 p-5 rounded-xl bg-[#121212] border border-[#27272A] space-y-4">

                      {/* Mode toggle (only for CRMs that support both) */}
                      {!crm.webhookOnly && (
                        <div className="flex gap-2">
                          <button data-testid={`mode-api-${crm.id}`}
                            onClick={() => setIntegrationMode('api')}
                            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                              integrationMode === 'api' ? 'bg-violet-600 text-white' : 'bg-[#1E1E1E] text-[#A1A1AA] border border-[#27272A]'
                            }`}>
                            Direct API
                          </button>
                          <button data-testid={`mode-webhook-${crm.id}`}
                            onClick={() => setIntegrationMode('webhook')}
                            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                              integrationMode === 'webhook' ? 'bg-violet-600 text-white' : 'bg-[#1E1E1E] text-[#A1A1AA] border border-[#27272A]'
                            }`}>
                            Webhook URL
                          </button>
                        </div>
                      )}

                      {/* Webhook mode */}
                      {integrationMode === 'webhook' && !crm.webhookOnly ? (
                        <div>
                          <Label className="text-[#A1A1AA] text-xs">Webhook URL</Label>
                          <Input data-testid={`webhook-url-${crm.id}`}
                            value={crmSettings.webhook_url || ''}
                            onChange={e => setCrmSettings(prev => ({ ...prev, webhook_url: e.target.value }))}
                            className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm"
                            placeholder="https://your-webhook-endpoint.com/..." />
                          <p className="text-[10px] text-[#525252] mt-1">We'll POST lead JSON to this URL</p>
                        </div>
                      ) : (
                        /* API fields */
                        <div className="space-y-3">
                          {crm.apiFields.map(f => (
                            <div key={f.key}>
                              <Label className="text-[#A1A1AA] text-xs">{f.label}</Label>
                              <Input data-testid={`field-${f.key}`}
                                type={f.type}
                                value={crmSettings[f.key] || ''}
                                onChange={e => setCrmSettings(prev => ({ ...prev, [f.key]: e.target.value }))}
                                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm"
                                placeholder={f.placeholder} />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Help link */}
                      <a href={crm.helpUrl} target="_blank" rel="noopener noreferrer"
                        data-testid={`help-${crm.id}`}
                        className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/10 hover:bg-blue-500/10 transition-colors">
                        <HelpCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-blue-400 text-xs font-medium">Where to find your credentials</p>
                          <p className="text-[#71717A] text-[11px] mt-0.5">{crm.helpText}</p>
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 text-blue-400/50 flex-shrink-0 mt-0.5" />
                      </a>

                      <Button data-testid={`save-crm-${crm.id}`} onClick={handleSaveCRM} disabled={saving}
                        className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 h-10">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                        {isConnected ? 'Update Connection' : `Connect ${crm.name}`}
                      </Button>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Generic Webhook */}
          <div className="mt-4 p-4 rounded-xl bg-[#121212] border border-dashed border-[#27272A]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#27272A] flex items-center justify-center">
                <Webhook className="w-5 h-5 text-[#A1A1AA]" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Custom Webhook</p>
                <p className="text-[#71717A] text-xs">Send leads to any endpoint via POST</p>
              </div>
            </div>
            <Input data-testid="custom-webhook-url"
              value={crmSettings.webhook_url || ''}
              onChange={e => setCrmSettings(prev => ({ ...prev, webhook_url: e.target.value }))}
              className="bg-[#1E1E1E] border-[#27272A] text-white text-sm"
              placeholder="https://your-custom-webhook.com/leads" />
            <Button data-testid="save-custom-webhook" size="sm" onClick={async () => {
              setSaving(true);
              try {
                await axios.put(`${API}/crm-settings`, { crm_type: 'webhook', integration_mode: 'webhook', webhook_url: crmSettings.webhook_url || '' });
                setCrmType('webhook');
                toast.success('Custom webhook saved!');
              } catch { toast.error('Failed to save'); }
              setSaving(false);
            }} disabled={saving} className="mt-2 bg-violet-600 hover:bg-violet-700 rounded-lg text-xs">
              Save Webhook
            </Button>
          </div>
        </div>
      )}

      {/* ═══ CONTACT FORM TAB ═══ */}
      {tab === 'contact' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-[#121212] border border-[#27272A] p-6 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="font-semibold text-lg text-white">Exchange Contact</h2>
              <p className="text-sm text-[#A1A1AA]">Let visitors share their details with you</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div>
              <p className="text-white font-medium text-sm">Enable Exchange Contact</p>
              <p className="text-xs text-[#71717A]">Show an exchange contact button on your profile</p>
            </div>
            <Switch data-testid="contact-form-toggle" checked={showContactForm} onCheckedChange={setShowContactForm} />
          </div>

          {showContactForm && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-[#A1A1AA] mb-3">Form Fields</p>
                <div className="space-y-2">
                  {BUILT_IN_FIELDS.map(field => {
                    const Icon = field.icon;
                    return (
                      <div key={field.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/[0.07] transition-colors">
                        <div className="flex items-center gap-3">
                          <Icon className="w-4 h-4 text-[#A1A1AA]" />
                          <span className="text-white text-sm">{field.label}</span>
                        </div>
                        <button data-testid={`toggle-field-${field.id}`} onClick={() => handleToggleField(field.id)}>
                          {fieldToggles[field.id] ? <ToggleRight className="w-7 h-7 text-violet-400" /> : <ToggleLeft className="w-7 h-7 text-[#555]" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-[#A1A1AA] mb-3">Custom Fields</p>
                <div className="space-y-2 mb-3">
                  {customFields.map(field => (
                    <div key={field.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <span className="text-white text-sm">{field.label}</span>
                      <div className="flex items-center gap-2">
                        <button data-testid={`toggle-custom-${field.id}`} onClick={() => handleToggleCustomField(field.id)}>
                          {field.enabled ? <ToggleRight className="w-7 h-7 text-violet-400" /> : <ToggleLeft className="w-7 h-7 text-[#555]" />}
                        </button>
                        <button onClick={() => handleRemoveCustomField(field.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="new-custom-field-input" value={newCustomField}
                    onChange={e => setNewCustomField(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddCustomField()}
                    placeholder="Add custom field name..." className="bg-[#1E1E1E] border-[#27272A] text-white text-sm" />
                  <Button data-testid="add-custom-field-btn" onClick={handleAddCustomField} size="sm"
                    className="bg-violet-500 hover:bg-violet-600 rounded-lg shrink-0">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-[#27272A] space-y-4">
            <div>
              <Label className="text-[#EDEDED] text-sm">WhatsApp Number (optional)</Label>
              <Input data-testid="whatsapp-number-input" value={whatsappNumber}
                onChange={e => setWhatsappNumber(e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="+1234567890" />
            </div>
            <div>
              <Label className="text-[#EDEDED] text-sm">WhatsApp Pre-filled Message (optional)</Label>
              <Input data-testid="whatsapp-message-input" value={whatsappMessage}
                onChange={e => setWhatsappMessage(e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Hi! I found you on One Link..." />
            </div>
          </div>

          <Button data-testid="save-contact-settings-btn" onClick={handleSaveContactSettings} disabled={saving}
            className="rounded-xl bg-violet-600 hover:bg-violet-700 w-full h-10">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {saving ? 'Saving...' : 'Save Contact Settings'}
          </Button>
        </motion.div>
      )}

      {/* ═══ REQUEST CRM TAB ═══ */}
      {tab === 'request' && (
        <div className="space-y-5">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-[#121212] border border-[#27272A] p-6 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Send className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h2 className="font-semibold text-lg text-white">Request a CRM Integration</h2>
                <p className="text-sm text-[#A1A1AA]">Don't see your CRM? Let us know and we'll add it</p>
              </div>
            </div>

            <div>
              <Label className="text-[#A1A1AA] text-xs">CRM Name *</Label>
              <Input data-testid="request-crm-name"
                value={requestName} onChange={e => setRequestName(e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white"
                placeholder="e.g. Close.io, Freshsales, Copper" />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs">Description (optional)</Label>
              <Textarea data-testid="request-crm-desc"
                value={requestDesc} onChange={e => setRequestDesc(e.target.value)}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white min-h-[80px]"
                placeholder="Tell us how you use this CRM and what integration you need..." />
            </div>
            <Button data-testid="submit-crm-request" onClick={handleSubmitRequest} disabled={requestSending}
              className="rounded-xl bg-amber-600 hover:bg-amber-700 h-10 w-full">
              {requestSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Request
            </Button>
          </motion.div>

          {/* Previous Requests */}
          {myRequests.length > 0 && (
            <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-5">
              <h3 className="text-white font-semibold text-sm mb-3">Your Requests</h3>
              <div className="space-y-2">
                {myRequests.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="text-white text-sm font-medium">{r.crm_name}</p>
                      {r.description && <p className="text-[#71717A] text-xs mt-0.5">{r.description}</p>}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      r.status === 'pending' ? 'bg-amber-500/15 text-amber-400' :
                      r.status === 'approved' ? 'bg-green-500/15 text-green-400' :
                      'bg-[#27272A] text-[#71717A]'
                    }`}>
                      {r.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CRMIntegration;
